import { useMemo } from 'react';

import { useLoaderData } from '@remix-run/react';
import {
  formatDate,
  processTimestampsInTiptap,
  type TAudioPlayerElement,
  updateMergeTagText,
} from '@shared/dream-components';

import type { PodcastEpisode } from '~/interfaces/podcast';

import { serializeNode } from '../serializeNode';
import type { NodeWithMergeTag } from './types';

const ALLOWED_MERGE_TAGS = [
  'podcast.episode.image',
  'podcast.episode.publish_date',
  'podcast.episode.title',
  'podcast.episode.description',
  'podcast.episode.duration',
  'podcast.episode.details',
  'podcast.episode.audio_player',
  'podcast.episode.tiptap_content',
  'podcast.episode.content',
  'podcast.show.name',
] as const;

type AllowedMergeTag = (typeof ALLOWED_MERGE_TAGS)[number];

interface UsePodcastMergeTagProcessorProps {
  node: NodeWithMergeTag;
  children: React.ReactNode;
}

interface ProcessedResult {
  node: NodeWithMergeTag;
  children: React.ReactNode;
}

type LoaderDataWithPodcastEpisode = {
  podcastEpisode?: PodcastEpisode | null;
};

const usePodcastMergeTagProcessor = ({ node, children }: UsePodcastMergeTagProcessorProps): ProcessedResult => {
  const loaderData = useLoaderData() as LoaderDataWithPodcastEpisode;
  const podcastEpisode = loaderData?.podcastEpisode;

  return useMemo(() => {
    const mergeTag = node.attrs?.mergeTag;

    // Early return if no merge tag or not an allowed merge tag
    if (!mergeTag || !ALLOWED_MERGE_TAGS.includes(mergeTag as AllowedMergeTag)) {
      return { node, children };
    }

    // Early return if no product present
    if (!podcastEpisode) {
      return { node, children };
    }

    let elementToRender: NodeWithMergeTag = node;

    switch (mergeTag) {
      case 'podcast.show.name':
        elementToRender = updateMergeTagText(node, podcastEpisode.show.title || '') as NodeWithMergeTag;
        break;
      case 'podcast.episode.image': {
        const primaryImageUrl = podcastEpisode.thumbnail_url;
        if (primaryImageUrl) {
          elementToRender = {
            ...node,
            attrs: {
              ...node.attrs,
              alt: podcastEpisode.title,
              src: primaryImageUrl,
            },
          } as NodeWithMergeTag;
        }
        break;
      }
      case 'podcast.episode.publish_date':
        elementToRender = updateMergeTagText(node, formatDate(podcastEpisode.publish_date)) as NodeWithMergeTag;
        break;
      case 'podcast.episode.title':
        elementToRender = updateMergeTagText(node, podcastEpisode.title || '') as NodeWithMergeTag;
        break;
      case 'podcast.episode.description': {
        const description = podcastEpisode.description || '';
        elementToRender = updateMergeTagText(node, description) as NodeWithMergeTag;
        break;
      }
      case 'podcast.episode.duration':
        elementToRender = updateMergeTagText(node, podcastEpisode.duration.toString()) as NodeWithMergeTag;
        break;
      case 'podcast.episode.content': {
        // Backwards compatibility to support RSS feeds that are rendered on path /podcast/$episodeSlug/$feedId
        // Encoded HTML is extracted from RSS feed and since we can't always have all the supported tags
        // it makes sense to render it as HTML
        const renderAsHTML = podcastEpisode.tiptap_content === null && podcastEpisode.description_html;
        // Process timestamps in show notes to make them clickable
        const processedContent = podcastEpisode.tiptap_content
          ? processTimestampsInTiptap(podcastEpisode.tiptap_content)
          : podcastEpisode.tiptap_content;

        elementToRender = {
          ...node,
          attrs: {
            ...node.attrs,
            renderAsHTML,
            html: podcastEpisode?.description_html ?? null,
            tiptapState: processedContent,
          },
        } as NodeWithMergeTag;
        break;
      }
      case 'podcast.episode.audio_player': {
        const audioNode = node as TAudioPlayerElement;
        elementToRender = {
          ...audioNode,
          attrs: {
            ...audioNode.attrs,
            data: {
              ...audioNode.attrs?.data,
              title: podcastEpisode.title,
              audioType: podcastEpisode.audio_type,
              audioUrl: podcastEpisode.audio_url,
              thumbnailUrl: podcastEpisode.thumbnail_url,
              waveform: podcastEpisode.waveform,
              duration: podcastEpisode.duration,
              youtubeVideoId: podcastEpisode.youtube_video_id ?? undefined,
            },
          },
        } as NodeWithMergeTag;
        break;
      }
      default:
        elementToRender = node;
    }

    const childrenToRender =
      'content' in elementToRender && elementToRender.content ? serializeNode(elementToRender.content) : children;

    return {
      node: elementToRender,
      children: childrenToRender,
    };
  }, [node, children, podcastEpisode]);
};

export default usePodcastMergeTagProcessor;
